import React, { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames/bind';
import { Button, KeyboardArrowLeftOutlinedIcon, DragHandleIcon } from 'tuui';
import { IframeWrapper } from 'modules/backstage-content/IframeWrapper';
import { tabId } from 'modules/taboola-common-frontend-modules/app-events-aggregator';
import { authTokenManager, config } from 'modules/taboola-common-frontend-modules/authentication';
import { useChatAgentContext } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useChatAgentContext';
import { useChatAgentSessionId } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useChatAgentSessionId';
import { PersistentResizable } from '../PersistentResizable/PersistentResizable';
import { useChatAgentMockUrl } from './hooks/useChatAgentMockUrl';
import { useChatAgentPermitted } from './hooks/useChatAgentPermitted';
import { useChatPromptVersion } from './hooks/useChatPromptVersion';
import styles from './ChatAgent.module.scss';

const classNameBuilder = classnames.bind(styles);

const COMMON_HEADERS = {
    Accept: 'text/html',
};

export const ChatAgent = () => {
    const { isChatOpened, closeChat, chatContainer, clearWindowList, isChatReady, client, chatRenderMode } =
        useChatAgentContext();
    const [sessionId] = useChatAgentSessionId();
    const [promptVersion] = useChatPromptVersion();
    const hasContainerRef = useRef(false);
    const isChatVisible = isChatOpened;
    const isPermitted = useChatAgentPermitted();
    const chatAgentMockUrl = useChatAgentMockUrl();
    const chatAgentChatUrl = config.getChatAgentChatUrl();
    const src = chatAgentMockUrl || chatAgentChatUrl || `${config.getChatAgentDomain()}/external-resources/chat-agent/`;
    const shouldRender = isChatReady && isPermitted && (config.getChatAgentDomain() !== '*' || !!chatAgentChatUrl);

    const authToken = authTokenManager.getToken();
    const headers = useMemo(
        () => ({
            ...COMMON_HEADERS,
            ...(shouldRender ? authTokenManager.getHeaders() : {}),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [authToken, shouldRender]
    );
    const documentForwardProps = useMemo(
        () => ({
            client,
            renderMode: chatRenderMode,
            promptVersion,
            tabId,
            hideHeader: !!chatContainer,
            sessionId,
            token: authToken,
        }),
        [client, chatRenderMode, promptVersion, chatContainer, sessionId, authToken]
    );

    if (!shouldRender || !authTokenManager.getToken()) {
        return null;
    }

    const iframeContent = (
        <IframeWrapper
            className={classNameBuilder('chat-iframe')}
            src={src}
            headers={headers}
            documentForwardProps={documentForwardProps}
            title="Chat Agent"
            isHeaderParamsEnabled
            baseHref={src}
        />
    );

    if (hasContainerRef.current !== !!chatContainer) {
        hasContainerRef.current = !!chatContainer;
        clearWindowList();
    }

    if (chatContainer) {
        return createPortal(iframeContent, chatContainer);
    }

    return (
        <div className={classNameBuilder('wrapper', { collapsed: !isChatVisible })}>
            <PersistentResizable className={classNameBuilder('inner')} persistencyKey="chatAgentSize">
                <div className={classNameBuilder('resize-icon')} fontSize="small">
                    <DragHandleIcon />
                </div>
                <div className={classNameBuilder('close-button')}>
                    <Button
                        className={classNameBuilder('back-button')}
                        size={Button.size.lg}
                        variant={Button.variant.ghost}
                        onClick={() => closeChat()}
                        aria-label="Close chat"
                    >
                        <KeyboardArrowLeftOutlinedIcon fontSize="large" className={classNameBuilder('icon')} />
                    </Button>
                </div>
                {iframeContent}
            </PersistentResizable>
        </div>
    );
};
